import axios from 'axios';
import { setLoadingState } from '../redux/db/actions';
import store from '../redux/store';

type LoadingStateKey =
	| 'user'
	| 'sceneIds'
	| 'scenes'
	| 'activeScene.frames'
	| 'activeScene.chats'
	| 'guest.scenes'
	| 'guest.frames';

export abstract class PollingStream<T> {
	protected aborter: AbortController | null = null;
	protected pollTimer: NodeJS.Timeout | null = null;
	protected lastTimestamp: string | null = null;
	protected isPolling = false;

	constructor(
		protected url: string,
		protected onError?: (error: Error) => void,
		protected loadingResource?: LoadingStateKey
	) {}

	start() {
		this.stop('cleanup'); // Clean up any existing polling

		// Set loading state if resource specified
		if (this.loadingResource) {
			store.dispatch(setLoadingState({ resource: this.loadingResource, state: 'loading' }));
		}

		this.aborter = new AbortController();
		this.isPolling = true;

		// Start the initial poll (will return immediately with all data)
		this.poll();
	}

	protected async poll() {
		if (!this.isPolling || !this.aborter) {
			return;
		}

		try {
			// Build URL with timestamp parameter if we have one
			const queryUrl = this.lastTimestamp
				? `${this.url}?since=${encodeURIComponent(this.lastTimestamp)}`
				: this.url;

			const response = await axios.get(queryUrl, {
				withCredentials: true,
				signal: this.aborter.signal,
			});

			const data = response.data;

			// Let subclass extract data and update timestamp
			const items = this.extractData(data);
			if (data.timestamp && typeof data.timestamp === 'string') {
				this.lastTimestamp = data.timestamp;
			}

			if (items.length > 0) {
				this.onData(items);
			}

			// Mark as loaded after first successful poll
			if (this.loadingResource) {
				store.dispatch(setLoadingState({ resource: this.loadingResource, state: 'loaded' }));
			}

			// Schedule next poll immediately (server does the long polling)
			if (this.isPolling) {
				this.pollTimer = setTimeout(() => this.poll(), 0);
			}
		} catch (error) {
			if (error instanceof Error) {
				// Ignore abort errors (they're expected when stopping)
				if (error.name === 'AbortError' || error.name === 'CanceledError') {
					return;
				}

				// Handle axios errors
				if (axios.isAxiosError(error)) {
					if (error.response?.status === 403) {
						// Access denied - call onError if provided
						const accessError = new Error('Access denied');
						if (this.onError) {
							this.onError(accessError);
						}
						this.stop('Access denied');
						return;
					}
				}

				console.error('Polling error:', error);
				if (this.onError) {
					this.onError(error);
				}

				// Retry after a delay on error (unless stopped)
				if (this.isPolling) {
					this.pollTimer = setTimeout(() => this.poll(), 5000); // 5 second delay on error
				}
			}
		}
	}

	stop(reason: string = 'Stream stopped') {
		this.isPolling = false;
		this.aborter?.abort(reason);
		this.aborter = null;

		if (this.pollTimer) {
			clearTimeout(this.pollTimer);
			this.pollTimer = null;
		}

		this.lastTimestamp = null;
	}

	// Abstract methods for subclasses to implement
	abstract extractData(response: any): T[];
	abstract onData(data: T[]): void;
}
